import { useWeb3React } from '@web3-react/core'
import BigNumber from 'bignumber.js'
import Balance from 'components/Balance'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { AutoColumn } from 'components/Layout/Column'
import { RowFixed } from 'components/Layout/Row'
import LocalBalance from 'components/LocalBalance'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import useToast from 'hooks/useToast'
import _ from 'lodash'
import React, { useState } from 'react'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import styled from 'styled-components'
import { BIG_ZERO } from 'utils/bigNumber'
import { formatNumber, getBalanceAmount } from 'utils/formatBalance'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import { FixedHeightRow, StyledText, StyledTooltipText } from 'views/xVvs/styles'
import { Text, Button, Flex, Box, AutoRenewIcon, useTooltip } from 'vvs-uikit'
import usePastWeekApr from 'views/xVvs/hooks/usePastWeekApr'
import PieChartSection from './PieChartSection'
import EarningTooltipContent from './EarningTooltipContent'

const WrapperCard = styled(Box)`
  border: 2px solid ${({ theme }) => theme.colors.outline};
  padding: 16px;
  border-radius: 16px;
  background: ${({ theme }) => (theme.isDark ? theme.colors.backgroundAlt2 : theme.colors.white)};
`

const EarningsBody: React.FC = () => {
  const { t } = useTranslation()
  const [pendingTx, setPendingTx] = useState<boolean>()
  const { theme } = useTheme()

  const { account } = useWeb3React()

  const { onDeposit } = useVvsBoostHandlers()

  const { boostData } = useUserVvsBoost()
  const { pendingVvs, stakes } = boostData
  const vaultsApr = useVaultsApr()
  const price = usePriceVvsUsdc()
  const xvvsPastWeekApr = usePastWeekApr()

  const totalLocked = stakes
    .filter((x) => x.active)
    .map((x) => x.amount)
    .reduce((a, b) => a.plus(b), BIG_ZERO)
  const totalUnlocked = stakes
    .filter((x) => x.active && x.unlockTimestamp <= new Date().getTime() / 1000)
    .map((x) => x.amount)
    .reduce((a, b) => a.plus(b), BIG_ZERO)

  const grouped = _.groupBy(
    boostData.stakes
      .map((x, i) => ({
        ...x,
        stakeId: i,
      }))
      .filter((x) => x.active),
    (p) => p.poolId,
  )

  const data = Object.keys(grouped).map((k) => ({
    pid: k,
    value: grouped[k]
      .map((x) => getBalanceAmount(x.amount, tokens.xvvs.decimals))
      .reduce((a, b) => a.plus(b), BIG_ZERO)
      .toNumber(),
  }))

  const weightedAmount = data
    .map((x) => {
      const apr = vaultsApr.find((pool) => pool.poolId === Number(x.pid))?.apr || '0'
      return new BigNumber(x.value).times(apr)
    })
    .reduce((a, b) => a.plus(b), BIG_ZERO)

  const totalAmount = data.reduce((a, b) => a + b.value, 0)
  const weightedApr = weightedAmount.div(totalAmount)
  const vaultApr = weightedApr.minus(xvvsPastWeekApr)

  const { toastError } = useToast()

  const addTransaction = useTransactionAdder()
  async function onHarvest() {
    setPendingTx(true)
    try {
      const depositRes = await onDeposit('0', '0')
      addTransaction(depositRes, {
        summary: `Your reward have been deposited to your wallet`,
      })
    } catch (err: any) {
      if (err?.code !== 4001) {
        console.error(err)
        toastError(t('Transaction Failed'), t('Please try again later'))
      }
    } finally {
      setPendingTx(false)
    }
  }

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    EarningTooltipContent({
      vaultApr: vaultApr && !vaultApr.isNaN() ? vaultApr.toFixed(3) : '-',
      xvvsPastWeekApr: xvvsPastWeekApr ? xvvsPastWeekApr.toFixed(3) : '-',
    }),
    {
      placement: 'bottom-start',
    },
  )

  return (
    <AutoColumn gap="8px" style={{ padding: '24px' }}>
      <FixedHeightRow>
        <RowFixed>
          {tooltipVisible && tooltip}
          <StyledTooltipText small textTransform="uppercase" ref={targetRef}>
            {t('Avg Earning APR:')}
          </StyledTooltipText>
        </RowFixed>
        <RowFixed>
          <Balance
            fontSize="24px"
            bold
            color="text"
            value={weightedApr.gt('0') ? weightedApr.toNumber() : 0}
            decimals={2}
            unit="%"
          />
        </RowFixed>
      </FixedHeightRow>

      <FixedHeightRow mt={3}>
        <RowFixed>
          <StyledText small>{t('xVVS Staked')}</StyledText>
        </RowFixed>
        <RowFixed>
          <Text bold ml="4px">
            {formatNumber(getBalanceAmount(totalLocked, tokens.xvvs.decimals).toNumber(), 3, 3)}
          </Text>
        </RowFixed>
      </FixedHeightRow>

      <FixedHeightRow mb={3}>
        <RowFixed>
          <StyledText small>{t('xVVS Unlocked')}</StyledText>
        </RowFixed>
        <RowFixed>
          <Text bold ml="4px">
            {formatNumber(getBalanceAmount(totalUnlocked, tokens.xvvs.decimals).toNumber(), 3, 3)}
          </Text>
        </RowFixed>
      </FixedHeightRow>

      {!account ? (
        <ConnectWalletButton scale="lg" />
      ) : (
        <Box borderTop={`2px solid ${theme.colors.backgroundAlt3}`} pt={3}>
          {getBalanceAmount(totalLocked, tokens.xvvs.decimals).gt('0') && <PieChartSection data={data} />}
          <WrapperCard mt={3}>
            <Flex>
              <Text fontSize="13px">{t('Pending Rewards Across Vaults')}</Text>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex flexDirection="column" mt="6px">
                <Text fontSize="18px" bold>{`${tokens.vvs.symbol} ${formatNumber(
                  getBalanceAmount(pendingVvs, tokens.vvs.decimals).toNumber(),
                  3,
                  3,
                )}`}</Text>
                <LocalBalance
                  fontSize="12px"
                  color="textSubtle"
                  decimals={2}
                  value={getBalanceAmount(pendingVvs, tokens.vvs.decimals).multipliedBy(price)}
                  prefix="~"
                />
              </Flex>
              <Button
                onClick={onHarvest}
                disabled={pendingVvs.isEqualTo('0')}
                isLoading={pendingTx}
                endIcon={pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
                ml="4px"
              >
                {t('Harvest')}
              </Button>{' '}
            </Flex>
          </WrapperCard>
        </Box>
      )}
    </AutoColumn>
  )
}

export default EarningsBody
